import React, { useState, useEffect, useContext } from 'react';
import { collection, query, addDoc, serverTimestamp, getDoc, updateDoc, doc, increment, DocumentData, Timestamp, QueryDocumentSnapshot, onSnapshot, arrayRemove, arrayUnion, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AuthContext from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { sanitizeText } from '../../utils/sanitize';
import { HeartIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';


interface Comment {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  createdAt: Timestamp | Date | null;
  likes: string[]; // Array de IDs de usuarios que dieron like
  likesCount: number; // Contador de likes
  parentId?: string | null; // ID del comentario padre (si es una respuesta) o null si es comentario principal
  replies?: number; // Contador de respuestas
}

interface CommentSectionProps {
  postId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ postId }: CommentSectionProps) => {
  // Definir un tipo para el usuario actual
  interface CurrentUser {
    uid: string;
    displayName: string | null;
    photoURL: string | null;
  }

  const { currentUser } = useContext(AuthContext) || { currentUser: null as CurrentUser | null };
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  const [userPhotoURL, setUserPhotoURL] = useState<string>('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [showReplies, setShowReplies] = useState<{ [key: string]: boolean }>({});
  const [loadingReplies, setLoadingReplies] = useState<{ [key: string]: boolean }>({});
  const [replies, setReplies] = useState<{ [key: string]: Comment[] }>({});

  // Cargar la información más reciente del usuario al montar el componente
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;

      try {
        // Obtener los datos más recientes del usuario desde Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserDisplayName(userData.displayName || currentUser.displayName || 'Usuario');
          setUserPhotoURL(userData.photoURL || currentUser.photoURL || '/default-avatar.png');
        } else {
          // Si no hay documento de usuario, usar los datos de Auth
          setUserDisplayName(currentUser.displayName || 'Usuario');
          setUserPhotoURL(currentUser.photoURL || '/default-avatar.png');
        }
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
        // En caso de error, usar los datos de Auth como respaldo
        setUserDisplayName(currentUser.displayName || 'Usuario');
        setUserPhotoURL(currentUser.photoURL || '/default-avatar.png');
      }
    };

    loadUserData();
  }, [currentUser]);

  useEffect(() => {
    setLoading(true);

    // Simplificamos la consulta para evitar problemas de índice
    // Primero obtenemos todos los comentarios
    const commentsQuery = query(
      collection(db, `posts/${postId}/comments`)
    );

    // Usar onSnapshot para escuchar cambios en tiempo real
    const unsubscribe = onSnapshot(
      commentsQuery,
      (querySnapshot) => {
        const fetchedComments: Comment[] = [];

        querySnapshot.forEach((docSnapshot: QueryDocumentSnapshot<DocumentData>) => {
          const commentData = docSnapshot.data() as Omit<Comment, 'id'>;
          // Verificar que el comentario tenga todos los campos necesarios antes de añadirlo
          if (commentData.userId && commentData.content) {
            // Asegurarse de que todos los campos requeridos estén inicializados
            fetchedComments.push({
              id: docSnapshot.id,
              userId: commentData.userId,
              userName: commentData.userName || 'Usuario',
              userPhotoURL: commentData.userPhotoURL || '/default-avatar.png',
              content: commentData.content,
              createdAt: commentData.createdAt,
              likes: commentData.likes || [],
              likesCount: commentData.likesCount || 0,
              replies: commentData.replies || 0,
              parentId: commentData.parentId || null  // Usar el valor real del parentId o null si no existe
            });
          }
        });

        // Filtrar solo los comentarios principales (sin parentId o parentId === null)
        const mainComments = fetchedComments.filter(comment => !comment.parentId);

        // Calcular el número real de respuestas para cada comentario principal
        const commentsWithRealReplyCount = mainComments.map(comment => ({
          ...comment,
          // Contar cuántos comentarios tienen este ID como parentId
          replies: fetchedComments.filter(c => c.parentId === comment.id).length
        }));

        // Ordenar por fecha de creación (más recientes primero)
        const sortedComments = commentsWithRealReplyCount.sort((a, b) => {
          const timeA = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() :
            a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
          const timeB = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() :
            b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
          return timeB - timeA; // Orden descendente (más recientes primero)
        });

        setComments(sortedComments);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching comments:', error);
        setLoading(false);
      }
    );

    // Limpiar el listener cuando el componente se desmonte
    return () => unsubscribe();
  }, [postId]);

  const handleSubmitComment = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!currentUser || !commentText.trim()) return;

    try {
      setSubmitting(true);

      // Sanitizar el contenido del comentario para prevenir XSS
      const sanitizedComment = sanitizeText(commentText.trim());

      // Obtener los datos más recientes del usuario desde Firestore
      // para asegurarnos de que estamos usando la información más actualizada
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      // Usar los valores actuales como valores predeterminados
      let userName = currentUser.displayName || 'Usuario';
      let photoURL = currentUser.photoURL || '/default-avatar.png';

      // Si el documento del usuario existe, usar esos datos en lugar de los del objeto currentUser
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userName = userData.displayName || userName;
        photoURL = userData.photoURL || photoURL;
      }

      // Add comment
      const commentData = {
        userId: currentUser.uid,
        userName: userName,
        userPhotoURL: photoURL,
        content: sanitizedComment,
        createdAt: serverTimestamp(),
        likes: [],
        likesCount: 0
      };

      // Añadir el comentario a Firestore y guardar la referencia
      const commentRef = await addDoc(collection(db, `posts/${postId}/comments`), commentData);

      // Update comment count in post
      await updateDoc(doc(db, 'posts', postId), {
        comments: increment(1)
      });

      // Ya no necesitamos actualizar el estado manualmente aquí
      // El listener onSnapshot detectará el nuevo comentario automáticamente

      // Reset form
      setCommentText('');

      // Get post data to notify the author
      const postDoc = await getDoc(doc(db, 'posts', postId));
      const postData = postDoc.exists() ? postDoc.data() : null;

      // Notify post author if it's not the current user
      if (postData && postData.userId !== currentUser.uid) {
        await addDoc(collection(db, 'notifications'), {
          type: 'comment',
          fromUserId: currentUser.uid,
          fromUserName: userName,
          fromUserPhoto: photoURL,
          toUserId: postData.userId,
          postId: postId,
          commentId: commentRef.id,
          content: sanitizedComment, // Usar el contenido sanitizado
          read: false,
          createdAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (!currentUser) return;

    try {
      // Crear una copia inmutable del estado actual para evitar problemas de renderizado
      const currentComments = [...comments];
      const commentIndex = currentComments.findIndex(c => c.id === commentId);

      if (commentIndex === -1) {
        console.error('Comentario no encontrado en el estado local');
        return;
      }

      // Obtener referencia al comentario en Firestore
      const commentRef = doc(db, `posts/${postId}/comments`, commentId);
      const commentDoc = await getDoc(commentRef);

      if (!commentDoc.exists()) {
        console.error('Comentario no encontrado en Firestore');
        return;
      }

      const commentData = commentDoc.data();
      const likes = commentData.likes || [];
      const userLiked = likes.includes(currentUser.uid);

      // Actualizar en Firestore
      if (userLiked) {
        // Quitar like
        await updateDoc(commentRef, {
          likes: arrayRemove(currentUser.uid),
          likesCount: increment(-1)
        });
      } else {
        // Agregar like
        await updateDoc(commentRef, {
          likes: arrayUnion(currentUser.uid),
          likesCount: increment(1)
        });

        // Notificar al autor del comentario si no es el usuario actual
        if (commentData.userId !== currentUser.uid) {
          try {
            const notificationsRef = collection(db, 'notifications');
            await addDoc(notificationsRef, {
              type: 'comment_like',
              fromUserId: currentUser.uid,
              fromUserName: userDisplayName,
              fromUserPhoto: userPhotoURL,
              toUserId: commentData.userId,
              postId: postId,
              commentId: commentId,
              content: commentData.content.substring(0, 50) + (commentData.content.length > 50 ? '...' : ''),
              read: false,
              createdAt: serverTimestamp()
            });
          } catch (notifError) {
            console.error('Error al crear notificación:', notifError);
            // No interrumpimos el flujo si falla la notificación
          }
        }
      }

      // Crear una nueva copia del comentario con los likes actualizados
      const updatedComment = {
        ...currentComments[commentIndex]
      };

      // Actualizar los likes del comentario
      if (userLiked) {
        updatedComment.likes = updatedComment.likes.filter(id => id !== currentUser.uid);
        updatedComment.likesCount = Math.max(0, updatedComment.likesCount - 1);
      } else {
        updatedComment.likes = [...updatedComment.likes, currentUser.uid];
        updatedComment.likesCount = updatedComment.likesCount + 1;
      }

      // Crear una nueva copia del array de comentarios
      const newComments = [...currentComments];
      newComments[commentIndex] = updatedComment;

      // Actualizar el estado con la nueva copia
      setComments(newComments);

    } catch (error) {
      console.error('Error al actualizar like:', error);
    }
  };

  const loadReplies = async (commentId: string) => {
    if (loadingReplies[commentId]) return;

    try {
      // Actualizar estado de carga
      setLoadingReplies(prev => ({ ...prev, [commentId]: true }));

      // Simplificar la consulta para evitar problemas de índice
      const repliesQuery = query(
        collection(db, `posts/${postId}/comments`)
      );

      const repliesSnapshot = await getDocs(repliesQuery);
      const fetchedReplies: Comment[] = [];

      // Filtrar las respuestas manualmente
      repliesSnapshot.forEach(doc => {
        const replyData = doc.data() as Omit<Comment, 'id'>;
        if (replyData.userId && replyData.content && replyData.parentId === commentId) {
          fetchedReplies.push({
            id: doc.id,
            userId: replyData.userId,
            userName: replyData.userName || 'Usuario',
            userPhotoURL: replyData.userPhotoURL || '/default-avatar.png',
            content: replyData.content,
            createdAt: replyData.createdAt,
            likes: replyData.likes || [],
            likesCount: replyData.likesCount || 0,
            parentId: replyData.parentId,
            replies: replyData.replies || 0
          });
        }
      });

      // Ordenar respuestas cronológicamente (más antiguas primero)
      const sortedReplies = fetchedReplies.sort((a, b) => {
        const timeA = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() :
          a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const timeB = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() :
          b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return timeA - timeB; // Orden ascendente (más antiguos primero)
      });

      // Actualizar el estado de respuestas con una nueva referencia de objeto
      setReplies(prev => {
        const newReplies = { ...prev };
        newReplies[commentId] = sortedReplies;
        return newReplies;
      });

      // Actualizar estado para mostrar las respuestas con una nueva referencia de objeto
      setShowReplies(prev => {
        const newShowReplies = { ...prev };
        newShowReplies[commentId] = true;
        return newShowReplies;
      });
    } catch (error) {
      console.error('Error al cargar respuestas:', error);
    } finally {
      // Actualizar estado de carga con una nueva referencia de objeto
      setLoadingReplies(prev => {
        const newLoadingReplies = { ...prev };
        newLoadingReplies[commentId] = false;
        return newLoadingReplies;
      });
    }
  };

  const handleSubmitReply = async (e: React.FormEvent<HTMLFormElement>, parentId: string) => {
    e.preventDefault();

    if (!currentUser || !replyText.trim() || !parentId) return;

    try {
      setSubmitting(true);

      // Sanitizar el contenido de la respuesta
      const sanitizedReply = sanitizeText(replyText.trim());

      // Obtener información actualizada del usuario
      const userName = userDisplayName || currentUser.displayName || 'Usuario';
      const photoURL = userPhotoURL || currentUser.photoURL || '/default-avatar.png';

      // Crear objeto de respuesta
      const replyData = {
        userId: currentUser.uid,
        userName: userName,
        userPhotoURL: photoURL,
        content: sanitizedReply,
        createdAt: serverTimestamp(),
        parentId: parentId,
        likes: [],
        likesCount: 0
      };

      // Añadir respuesta a Firestore
      const replyRef = await addDoc(collection(db, `posts/${postId}/comments`), replyData);

      // Incrementar contador de respuestas en el comentario padre
      const parentCommentRef = doc(db, `posts/${postId}/comments`, parentId);
      await updateDoc(parentCommentRef, {
        replies: increment(1)
      });

      // Actualizar el estado inmediatamente para mejor UX
      const replyWithLocalDate: Comment = {
        id: replyRef.id,
        userId: currentUser.uid,
        userName: userName,
        userPhotoURL: photoURL,
        content: sanitizedReply,
        createdAt: new Date(),
        parentId: parentId,
        likes: [],
        likesCount: 0
      };

      // Actualizar lista de respuestas
      setReplies(prev => ({
        ...prev,
        [parentId]: [...(prev[parentId] || []), replyWithLocalDate]
      }));

      // Actualizar contador de respuestas en el comentario padre
      setComments(prevComments =>
        prevComments.map(comment => {
          if (comment.id === parentId) {
            return {
              ...comment,
              replies: (comment.replies || 0) + 1
            };
          }
          return comment;
        })
      );

      // Notificar al autor del comentario si no es el usuario actual
      const parentComment = comments.find(c => c.id === parentId);
      if (parentComment && parentComment.userId !== currentUser.uid) {
        const notificationsRef = collection(db, 'notifications');
        await addDoc(notificationsRef, {
          type: 'comment_reply',
          fromUserId: currentUser.uid,
          fromUserName: userName,
          fromUserPhoto: photoURL,
          toUserId: parentComment.userId,
          postId: postId,
          commentId: replyRef.id,
          parentCommentId: parentId,
          content: sanitizedReply.substring(0, 50) + (sanitizedReply.length > 50 ? '...' : ''),
          read: false,
          createdAt: serverTimestamp()
        });
      }

      // Limpiar formulario
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error al añadir respuesta:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCommentDate = (timestamp: Timestamp | Date | null): string => {
    if (!timestamp) return 'ahora mismo';

    try {
      // Si es un Timestamp de Firestore
      if (timestamp instanceof Timestamp) {
        return formatDistanceToNow(timestamp.toDate(), { addSuffix: true, locale: es });
      }
      // Si es un Date de JavaScript
      else if (timestamp instanceof Date) {
        return formatDistanceToNow(timestamp, { addSuffix: true, locale: es });
      }
      // Si es otro tipo, intentar convertirlo
      return formatDistanceToNow(new Date(timestamp as any), { addSuffix: true, locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'hace un momento';
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-medium">Comentarios ({comments.length})</h3>

      {/* Comment form */}
      {currentUser && (
        <form onSubmit={handleSubmitComment} className="flex items-start space-x-2">
          <img
            src={userPhotoURL || '/default-avatar.png'}
            alt={userDisplayName || 'User'}
            className="h-8 w-8 rounded-full object-cover"
          />
          <div className="flex-grow">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Escribe un comentario..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700 text-sm"
              rows={2}
              required
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={submitting || !commentText.trim()}
                className="px-3 py-1 bg-domired-600 text-white text-sm font-medium rounded-md hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
              >
                {submitting ? 'Enviando...' : 'Comentar'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Comments list */}
      {loading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-domired-600"></div>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-3">
          {comments.map((comment: Comment) => comment && comment.content && (
            <div key={comment.id} className="flex space-x-2">
              <Link to={`/profile/${comment.userId}`} className="flex-shrink-0">
                <img
                  src={comment.userPhotoURL || '/default-avatar.png'}
                  alt={comment.userName}
                  className="h-8 w-8 rounded-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = '/default-avatar.png';
                  }}
                />
              </Link>
              <div className="flex-grow bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                <div className="flex items-baseline justify-between">
                  <Link to={`/profile/${comment.userId}`} className="font-medium text-sm hover:underline">
                    {comment.userName}
                  </Link>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatCommentDate(comment.createdAt)}
                  </span>
                </div>
                <p className="text-sm mt-1">{comment.content}</p>
                <div className="flex space-x-4 mt-2">
                  <button
                    onClick={() => handleLikeComment(comment.id)}
                    className={`flex items-center text-sm transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95 ${(comment.likes || []).includes(currentUser?.uid || '')
                      ? 'text-domired-600 dark:text-domired-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400'}`}
                    aria-label={(comment.likes || []).includes(currentUser?.uid || '') ? 'Quitar like' : 'Me gusta'}
                  >
                    {(comment.likes || []).includes(currentUser?.uid || '')
                      ? <HeartSolidIcon className="h-4 w-4 text-domired-600 dark:text-domired-400 animate-heart-beat" />
                      : <HeartIcon className="h-4 w-4 transition-transform duration-300 ease-in-out hover:scale-110" />}
                    {comment.likesCount > 0 && (
                      <span className={`ml-1 text-xs ${(comment.likes || []).includes(currentUser?.uid || '') ? 'text-domired-600 dark:text-domired-400' : 'text-gray-500 dark:text-gray-400'}`}>{comment.likesCount}</span>
                    )}
                  </button>
                  <button
                    onClick={() => setReplyingTo(comment.id)}
                    className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400"
                  >
                    <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                    Responder
                  </button>
                </div>
                {replyingTo === comment.id && (
                  <form onSubmit={(e) => handleSubmitReply(e, comment.id)} className="mt-2">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Escribe una respuesta..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700 text-sm"
                      rows={2}
                      required
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        type="submit"
                        disabled={submitting || !replyText.trim()}
                        className="px-3 py-1 bg-domired-600 text-white text-sm font-medium rounded-md hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
                      >
                        {submitting ? 'Enviando...' : 'Responder'}
                      </button>
                    </div>
                  </form>
                )}
                {showReplies[comment.id] && (
                  <div className="mt-2">
                    {replies[comment.id] && replies[comment.id].map(reply => (
                      <div key={reply.id} className="flex space-x-2 mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                        <Link to={`/profile/${reply.userId}`} className="flex-shrink-0">
                          <img
                            src={reply.userPhotoURL || '/default-avatar.png'}
                            alt={reply.userName}
                            className="h-8 w-8 rounded-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = '/default-avatar.png';
                            }}
                          />
                        </Link>
                        <div className="flex-grow bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                          <div className="flex items-baseline justify-between">
                            <Link to={`/profile/${reply.userId}`} className="font-medium text-sm hover:underline">
                              {reply.userName}
                            </Link>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatCommentDate(reply.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm mt-1">{reply.content}</p>
                          <div className="flex space-x-4 mt-2">
                            <button
                              onClick={() => handleLikeComment(reply.id)}
                              className={`flex items-center text-xs font-medium transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95 ${(reply.likes || []).includes(currentUser?.uid || '')
                                ? 'text-domired-600 dark:text-domired-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400'}`}
                            >
                              {(reply.likes || []).includes(currentUser?.uid || '')
                                ? <HeartSolidIcon className="h-3 w-3 mr-1 text-domired-600 dark:text-domired-400 animate-heart-beat" />
                                : <HeartIcon className="h-3 w-3 mr-1 transition-transform duration-300 ease-in-out hover:scale-110" />}
                              {(reply.likes || []).includes(currentUser?.uid || '') ? 'Quitar like' : 'Me gusta'}
                              {reply.likesCount > 0 && (
                                <span className={`ml-1 text-xs font-medium ${(reply.likes || []).includes(currentUser?.uid || '') ? 'text-domired-600 dark:text-domired-400' : 'text-gray-500 dark:text-gray-400'}`}>({reply.likesCount})</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!showReplies[comment.id] && (comment.replies ?? 0) > 0 && (
                  <button
                    onClick={() => loadReplies(comment.id)}
                    className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400 mt-2"
                  >
                    <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                    Ver respuestas ({comment.replies ?? 0})
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 dark:text-gray-400 text-sm py-2">
          No hay comentarios aún. Sé el primero en comentar.
        </p>
      )}
    </div>
  );
};

export default CommentSection;
